import { useActionConfirmation } from '../../components/ActionFeedbackProvider';
// React and hooks
import React, { useState, useEffect } from 'react';
// Icons
import { Trash, Plus, ChevronLeft, ChevronRight, Search, Pencil, FileDown } from 'lucide-react';
import { FaWarehouse } from 'react-icons/fa';
// Router
import { useNavigate } from 'react-router-dom';
// Animations
import { motion } from 'framer-motion';
// Hooks
import useTheme from '../../hooks/useTheme';
// API client
import api from '../../lib/api';
// Modal component
import AddEntryModal from '../../components/modals/AddEntryModal';
// NEW: Edit modal
import EditEntryModal from '../../components/modals/EditEntryModal';
// i18n
import { useTranslation } from 'react-i18next';

const EntrySWarehouse = () => {
  // States
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);

  // NEW: edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');

  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();
  const confirmAction = useActionConfirmation();

  const { theme } = useTheme();

  // Fixed key for this screen
  const locationKey = 'warehouse';

  // General button style (used by Add Entry, Inventory)
  const baseBtn =
    'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-200 ' +
    'bg-black text-white hover:bg-white hover:text-black hover:scale-105 ' +
    'dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white';

  // Medium action buttons (edit/export/delete)
  const actionBtn =
    'inline-flex items-center justify-center p-2.5 rounded-md shadow transition duration-200 text-sm ' +
    'bg-black text-white hover:bg-white hover:text-black hover:scale-105 ' +
    'dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white';

  // Helper to get ID safely
  const getId = (obj) => obj?.id || obj?._id;

  // Format date to yyyy-mm-dd for filtering
  const formatDate = (date) => date.toISOString().split('T')[0];

  // Get day name (e.g. Tuesday)
  const getDayName = (date) =>
    date.toLocaleDateString(undefined, { weekday: 'long' });

  // Change day backward or forward
  const changeDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  // Filter entries by selected date + search term
  const filterEntries = (entriesList, date, term) => {
    const filtered = entriesList.filter((entry) => {
      const entryDate = new Date(entry.enteredAt);
      const sameDay =
        entryDate.getFullYear() === date.getFullYear() &&
        entryDate.getMonth() === date.getMonth() &&
        entryDate.getDate() === date.getDate();

      const searchMatch =
        (entry.productName || '').toLowerCase().includes(term.toLowerCase()) ||
        (entry.brandName || '').toLowerCase().includes(term.toLowerCase()) ||
        (entry.supplierName || '').toLowerCase().includes(term.toLowerCase()) ||
        (entry.notes || '').toLowerCase().includes(term.toLowerCase());

      return sameDay && searchMatch;
    });
    setFilteredEntries(filtered);
  };

  // Load entries from backend
  const loadEntries = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/inventory/entries?location=${locationKey}`);
      const arr = Array.isArray(data) ? data.slice().reverse() : [];
      setEntries(arr);
      filterEntries(arr, selectedDate, searchTerm);
    } catch (err) {
      setError(err?.response?.data?.message || t('errorLoadEntries'));
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadEntries();
  }, []);

  // Refilter when date or search term changes
  useEffect(() => {
    filterEntries(entries, selectedDate, searchTerm);
  }, [entries, selectedDate, searchTerm]);

  // Delete entry by ID
  const handleDelete = async (id) => {
    if (!await confirmAction(t('confirmDeleteEntry'))) return;
    try {
      await api.delete(`/inventory/entries/${id}`);
      await loadEntries();
    } catch (err) {
      setError(err?.response?.data?.message || t('errorDeleteEntry'));
    }
  };

  // Export entry weights (Excel)
  const handleExport = async (id) => {
    try {
      const response = await api.get(`/inventory/entries/${id}/export`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `entry_${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert(t('errorExport') || 'Error exporting entry');
    }
  };

  // Edit entry (NOW WORKING)
  const handleEdit = (entry) => {
    setSelectedRecord(entry);
    setShowEditModal(true);
  };

  // Render
  return (
    <motion.div className="px-1 py-4 sm:px-3 sm:py-6 lg:px-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
      
      {/* Date selector at top */}
      <div className="flex flex-col items-center mb-6 select-none">
        <div className="flex items-center gap-2">
          <button onClick={() => changeDate(-1)} className="p-2 rounded-md bg-black text-white dark:bg-white dark:text-black hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white transition">
            <ChevronLeft size={18} />
          </button>

          <input type="date" value={formatDate(selectedDate)} onChange={(e) => setSelectedDate(new Date(e.target.value))}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 text-sm dark:bg-gray-800 dark:text-white" />

          <button onClick={() => changeDate(1)} className="p-2 rounded-md bg-black text-white dark:bg-white dark:text-black hover:bg-white hover:text-black dark:hover:bg-black dark:hover:text-white transition">
            <ChevronRight size={18} />
          </button>
        </div>

        <h2 className="mt-2 text-xl font-bold text-gray-900 dark:text-white capitalize">
          {getDayName(selectedDate)}
        </h2>
      </div>

      {/* Screen title */}
      <h1 className="text-2xl font-bold text-center mb-4 text-gray-900 dark:text-white">
        {t('entriesWarehouse')}
      </h1>

      {/* Search bar */}
      <div className="max-w-3xl mx-auto mb-6 relative">
        <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input type="text" placeholder={t('searchPlaceholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white" />
      </div>

      {/* Main card */}
      <div className="max-w-5xl mx-auto bg-white dark:bg-gray-800 shadow-xl rounded-xl p-3 sm:p-4 lg:p-6">
        
        {/* Header actions */}
        <div className="flex flex-col gap-3 mb-5 sm:flex-row sm:items-center sm:justify-between">
          <div />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowModal(true)} className={baseBtn}>
              <Plus size={16} /> {t('addEntry')}
            </button>
            <button onClick={() => navigate('/products/inventory/warehouse')} className={baseBtn}>
              <FaWarehouse /> {t('inventory')}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && <div className="mb-3 rounded-lg bg-red-600/90 text-white px-4 py-2">{error}</div>}

        {/* Entries filtered by selected date */}
        {loading ? (
          <div className="text-gray-700 dark:text-gray-200">{t('loading')}</div>
        ) : filteredEntries.length > 0 ? (
          <div className="space-y-2">
            {filteredEntries.map((entry) => (
              <div key={getId(entry)} className="border border-gray-300 dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-700 shadow-sm flex justify-between items-center">
                
                {/* Entry details */}
                <div className="flex flex-col space-y-0.5 text-sm w-full dark:text-gray-200">
                  <p className="font-semibold text-base truncate dark:text-white">{entry.productName}</p>

                  <p><span className="font-medium">{t('brand')}:</span> {entry.brandName}</p>
                  <p><span className="font-medium">{t('supplier')}:</span> {entry.supplierName}</p>

                  <p>
                    <span className="font-medium">{t('total')}:</span> {(entry.totalWeight ?? 0).toFixed(2)} KG •{' '}
                    <span className="font-medium">{t('qty')}:</span> {entry.quantityEntered}
                  </p>

                  {entry.notes && <p className="italic"><span className="font-medium">{t('notes')}:</span> {entry.notes}</p>}

                  <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(entry.enteredAt).toLocaleString()}</p>
                  {entry.userId && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium">{t('createdBy')}:</span> {entry.userId}
                    </p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex flex-row gap-2 ml-4 shrink-0">
                  <button onClick={() => handleEdit(entry)} className={actionBtn}><Pencil size={17} /></button>
                  <button onClick={() => handleExport(getId(entry))} className={actionBtn}><FileDown size={17} /></button>
                  <button onClick={() => handleDelete(getId(entry))} className={actionBtn}><Trash size={17} /></button>
                </div>

              </div>
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-gray-300 dark:border-gray-700 p-5 rounded-md text-center dark:text-gray-300">
            {t('noEntriesForThisDate')}
          </div>
        )}
      </div>

      {/* Modal for adding entries */}
      {showModal && (
        <AddEntryModal isOpen={showModal} onClose={() => setShowModal(false)} onCreated={loadEntries} locationKey={locationKey} />
      )}

      {/* NEW: Edit modal */}
      {showEditModal && (
        <EditEntryModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          record={selectedRecord}
          locationKey={locationKey}
          onUpdated={loadEntries}
        />
      )}
    </motion.div>
  );
};

export default EntrySWarehouse;
